import { useState, memo, useEffect, useRef } from "react";
import { Card, Typography, Box, IconButton, Divider, CircularProgress } from "@mui/joy";
import { ImageUp, UnfoldVertical } from "lucide-react";
import { ImageUploadDropzone } from "./ImageUploadDropzone";
import { ImagePreviewModal } from "./ImagePreviewDialog";
import { ImageUploadModal } from "./ImageUploadModal";
import { useTask } from "../../hooks/query";
import { useNotification } from "../../hooks";

interface ImageWithMetadata {
    url: string;
    assetId?: string;
    note?: string | null;
}

interface AlbumProps {
    coverImage: string;
    title: string;
    description: string;
    images: string[] | ImageWithMetadata[];
    label: string;
    projectId: string;
    taskId: string;
}

interface GalleryViewProps {
    title: string;
    images: ImageWithMetadata[];
    onPreview: (image: ImageWithMetadata | string) => void;
    onAddImages: (files: File[]) => void;
    uploadStatus?: any;
}

const AlbumWidget = (props: AlbumProps) => {
    const { coverImage, title, description, images: stateParentImages, label, taskId, projectId } = { ...props };
    const { useUploadAsset } = useTask();
    const uploadAssetSvc = useUploadAsset();
    const { showError, showSuccess } = useNotification();

    const [expandGallery, setExpandGallery] = useState(false);
    const [selectedImage, setSelectedImage] = useState<ImageWithMetadata | null>(null);
    const [images, setImages] = useState<ImageWithMetadata[]>(() => {
        // Convert string array to ImageWithMetadata array for backward compatibility
        if (!stateParentImages || stateParentImages.length === 0) return [];
        return stateParentImages.map((img: string | ImageWithMetadata) => {
            if (typeof img === "string") {
                return { url: img };
            }
            return img;
        });
    });
    const [newImageUploadStatus, setNewImageUploadStatus] = useState<any>({});
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const toggleExpandGallery = () => setExpandGallery((prev) => !prev);

    const handlePreview = (image: ImageWithMetadata | string) => {
        if (typeof image === "string") {
            setSelectedImage({ url: image });
        } else {
            setSelectedImage(image);
        }
    };

    const closePreview = () => {
        setSelectedImage(null);
    };

    const uploadImage = async (files: File[], note: string = "") => {
        let successCount = 0;
        let errorCount = 0;

        for (const file of files) {
            try {
                if (!newImageUploadStatus[file.name]) {
                    let data = new FormData();
                    data.append("File", file);
                    data.append("Label", label);
                    data.append("TaskId", taskId);
                    data.append("FileName", file.name);
                    data.append("ProjectId", projectId);
                    if (note) {
                        data.append("Note", note);
                    }
                    await uploadAssetSvc.mutateAsync(data);
                    setNewImageUploadStatus((prevValue: any) => ({ ...prevValue, [file.name]: true }));
                    successCount++;
                }
            } catch (e) {
                console.error(e);
                errorCount++;
            }
        }

        // Show success/error messages
        if (successCount > 0 && errorCount === 0) {
            if (successCount === 1) {
                showSuccess("Image uploaded successfully");
            } else {
                showSuccess(`${successCount} images uploaded successfully`);
            }
        } else if (errorCount > 0) {
            showError(`Failed to upload ${errorCount} image(s)`);
        }
    };

    const handleFilesSelected = (files: File[]) => {
        if (!files || files.length === 0) {
            return;
        }

        // Filter out files already uploaded
        const duplicateFiles = files.filter((file) => newImageUploadStatus[file.name]);
        const newFiles = files.filter((file) => !newImageUploadStatus[file.name]);

        // Show error if all files are duplicates
        if (newFiles.length === 0 && duplicateFiles.length > 0) {
            if (duplicateFiles.length === 1) {
                showError(`"${duplicateFiles[0].name}" has already been uploaded`);
            } else {
                showError(`${duplicateFiles.length} images have already been uploaded`);
            }
            return;
        }

        // Show warning if some files are duplicates
        if (duplicateFiles.length > 0) {
            if (duplicateFiles.length === 1) {
                showError(`"${duplicateFiles[0].name}" has already been uploaded and will be skipped`);
            } else {
                showError(`${duplicateFiles.length} images have already been uploaded and will be skipped`);
            }
        }

        // Show modal with only new files (if any)
        if (newFiles.length > 0) {
            setPendingFiles(newFiles);
            setUploadModalOpen(true);
        }
    };

    const handleUploadFromModal = async (files: File[], note: string) => {
        // Create preview URLs for immediate UI feedback
        const newImageUrls: ImageWithMetadata[] = files.map((file) => ({
            url: URL.createObjectURL(file),
            note: note || null,
        }));
        setImages((prev) => [...prev, ...newImageUrls]);
        // Upload with note
        await uploadImage(files, note);
    };

    const handleUploadButtonClick = () => {
        // Reset input value before clicking to ensure onChange fires
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        // Small delay to ensure reset is processed
        setTimeout(() => {
            fileInputRef.current?.click();
        }, 10);
    };

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (files.length > 0) {
            handleFilesSelected(files);
        }
        // Reset input after processing so same file can be selected again
        // Use setTimeout to ensure the change event is fully processed
        setTimeout(() => {
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }, 0);
    };

    // Update images when stateParentImages changes (e.g., after server refresh)
    useEffect(() => {
        if (stateParentImages && stateParentImages.length > 0) {
            const convertedImages = stateParentImages.map((img: string | ImageWithMetadata) => {
                if (typeof img === "string") {
                    return { url: img };
                }
                return img;
            });
            setImages(convertedImages);
        }
    }, [stateParentImages]);

    useEffect(() => {
        return () => {
            images?.forEach((image) => {
                const url = typeof image === "string" ? image : image.url;
                if (url.startsWith("blob:")) {
                    URL.revokeObjectURL(url);
                }
            });
        };
    }, [images]);

    return (
        <Box>
            <Card variant="soft" sx={galleryStyles.albumWidgetWrapper}>
                <Card
                    sx={{
                        ...galleryStyles.albumCoverImage,
                        backgroundImage: `url('${
                            images && images.length > 0
                                ? typeof images[0] === "string"
                                    ? images[0]
                                    : images[0].url
                                : coverImage
                        }')`,
                    }}
                    role="img"
                    aria-label={`${title} Cover Image`}
                />

                <Box flex={1}>
                    <Typography level="body-md" sx={{ fontWeight: 500 }}>
                        {title}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary", fontWeight: 400 }} gutterBottom>
                        {description}
                    </Typography>
                    <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                        {images?.length ?? 0} Photos
                    </Typography>
                </Box>

                <Box>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        multiple
                        style={{ display: "none" }}
                        onChange={handleFileInputChange}
                    />
                    <IconButton variant="outlined" aria-label="Upload photo" onClick={handleUploadButtonClick}>
                        <ImageUp size={16} />
                    </IconButton>
                </Box>

                <Box>
                    <IconButton variant="outlined" aria-label="Toggle gallery view" onClick={toggleExpandGallery}>
                        <UnfoldVertical size={16} />
                    </IconButton>
                </Box>
            </Card>

            {expandGallery && (
                <Box ml={2} display="flex">
                    <Divider orientation="vertical" sx={{ px: 0.2 }} />
                    <Box ml={2} mb={1} flex={1}>
                        <GalleryView
                            title={title}
                            images={images}
                            onPreview={handlePreview} // your own preview logic
                            onAddImages={handleFilesSelected}
                            uploadStatus={newImageUploadStatus}
                        />
                    </Box>
                </Box>
            )}

            <ImagePreviewModal image={selectedImage} onClose={closePreview} />
            <ImageUploadModal
                open={uploadModalOpen}
                files={pendingFiles}
                onClose={() => {
                    setUploadModalOpen(false);
                    setPendingFiles([]);
                    // Reset file input so same file can be selected again
                    if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                    }
                }}
                onUpload={handleUploadFromModal}
            />
        </Box>
    );
};

const GalleryView = memo((props: GalleryViewProps) => {
    const { title, images, onPreview, onAddImages, uploadStatus } = { ...props };
    const [loading, setLoading] = useState(true);
    return (
        <Box sx={{ mt: 1 }}>
            <Typography level="title-md" sx={{ mb: 1 }}>
                {title}
            </Typography>

            <Box sx={galleryStyles.grid}>
                {images.map((image, index) => {
                    const imageUrl = typeof image === "string" ? image : image.url;
                    return (
                        <Card key={index} sx={{ p: 0, gap: 0, position: "relative" }}>
                            {loading && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        backgroundColor: "#f0f0f0",
                                        borderRadius: 8,
                                    }}
                                >
                                    <CircularProgress size="sm" />
                                </div>
                            )}

                            <img
                                src={imageUrl}
                                alt={`Gallery image ${index + 1}`}
                                style={{
                                    ...galleryStyles.image,
                                    opacity: loading ? 0 : 1,
                                    transition: "opacity 0.3s ease-in-out",
                                }}
                                loading="lazy"
                                onLoad={() => setLoading(false)}
                                onClick={() => onPreview(image)}
                            />
                            {/* {uploadStatus[image.name] && <ImageUploading />} */}
                        </Card>
                    );
                })}

                <ImageUploadDropzone onFilesAdded={onAddImages} />
            </Box>
        </Box>
    );
});

GalleryView.displayName = "GalleryView";

function ImageUploading() {
    return (
        <Box
            sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(216, 216, 216, 0.63)",
                borderRadius: 10,
            }}
        >
            <CircularProgress size="sm" />
        </Box>
    );
}

const galleryStyles = {
    albumWidgetWrapper: {
        display: "flex",
        border: "1px solid",
        borderColor: "divider",
        py: 1,
        px: 1,
        pr: 2,
        gap: 2,
        flexDirection: "row",
        alignItems: "center",
    },
    albumCoverImage: {
        p: 0,
        width: 70,
        height: 70,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 2,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        flexShrink: 0,
    },
    grid: {
        display: "flex",
        flexWrap: "wrap",
        gap: 2,
        justifyContent: "flex-start",
    },
    image: {
        width: 150,
        height: 150,
        borderRadius: 8,
        cursor: "pointer",
        objectFit: "cover",
        transition: "transform 0.2s",
        "::hover": {
            transform: "scale(1.03)",
        },
    } as const,
};

export default AlbumWidget;
